import React from 'react';
import styled from 'styled-components';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { SortDirection, TextSortOrder } from '../../shared/enum/EnumRepository';
import { useTextListStore } from '../../shared/store/TextListStore';
import { useUnsavedChanges } from '../../shared/hooks/useUnsavedChanges';

interface BlockWithSortVisibleProps {
  width: string;
  isColumn?: boolean;
  field: TextSortOrder;
  value: string;
}

const BlockWithSortVisible: React.FC<BlockWithSortVisibleProps> = ({ width, isColumn, field, value }) => {
  const { isEditMode } = useEditMode();
  const { requestExitEditMode } = useUnsavedChanges();
  const sortData = useTextListStore((s) => s.sortData);
  const setSortData = useTextListStore((s) => s.setSortData);

  const handleDirectionChange = (field: TextSortOrder) => {
    if (isEditMode) {
      requestExitEditMode({ confirmWhenClean: true });
      return;
    }

    // 현재 정렬 필드와 같으면 방향만 변경, Year는 최신순(DESC)으로 시작
    if (sortData.sort === field) {
      setSortData({
        sort: field,
        direction: sortData.direction === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC
      });
    } else {
      setSortData({
        sort: field,
        direction: field === TextSortOrder.YEAR ? SortDirection.DESC : SortDirection.ASC,
      });
    }
  };

  return (
    <Block
      $width={width}
      $isColumn={isColumn}
      onClick={() => handleDirectionChange(field)}
    >
      <ValueContainer>
        <Value
          $isColumn={isColumn}
          $isVisible={true}
        >
          {value}
        </Value>
        <SelectDirection
          $isASC={sortData.sort === field && sortData.direction === SortDirection.ASC}
          $isSortField={sortData.sort === field}
        >
          ▼
        </SelectDirection>
      </ValueContainer>
    </Block>
  )
}


const Block = styled.div<{ $width: string; $isColumn: boolean | undefined }>`
  flex: ${({ $width }) => $width};

  display: flex;
  justify-content: center;
  align-items: center;

  min-width: 0;

  cursor: pointer;
  user-select: none;

  border-bottom: ${({ $isColumn, theme }) =>
    $isColumn ? 'none' : `0.5px solid ${theme.colors.color_Gray_04}`};
`

const ValueContainer = styled.div`
  width: 80%;
  display: flex;
  justify-content: center;
  align-items: center;
`

const Value = styled.span<{ $isVisible: boolean; $isColumn: boolean | undefined }>`
  white-space: nowrap;
  overflow-x: hidden;
  text-overflow: ellipsis;

  padding-left: ${({ $isColumn }) => $isColumn ? `10px` : '3px'};

  text-align: center;

  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0.5)};
  user-select: none;
  cursor: pointer;
`

const SelectDirection = styled.span<{ $isSortField: boolean, $isASC: boolean }>`
  margin-left: 4px;
  ${({ theme }) => theme.typography.Footer_02};
  color: ${({ theme, $isSortField }) => $isSortField ? theme.colors.color_Gray_03 : theme.colors.color_Gray_05};
  transform: ${({ $isASC, $isSortField }) => {
    if (!$isSortField) return "scaleY(1) scale(0.7)"; // 기본: dim 처리된 ▼
    return $isASC ? "scaleY(-1) scale(0.7)" : "scaleY(1) scale(0.7)"; // ASC면 뒤집기, DESC면 그대로
  }} !important;
  transition: transform 0.6s ease-in-out;
  display: inline-block;
`;

export default BlockWithSortVisible;
